import React, { useState, useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import { Image, Menu as SemanticMenu, Responsive, Icon } from 'semantic-ui-react';
import styled from 'styled-components';

import packageInfo from '../../package.json';
import { menuOptions } from '../services/menu';
import { MenuItemProps } from './types';

import MenuItem from './MenuItem';

import logo from '../resources/imgs/logo-lado-bom-seguros.svg';
import partialLogo from '../resources/imgs/partial-logo-02.svg';

import { iRootState, iRootDispatch } from '../store';
import AuthorizationContainer from '../components/AuthorizationContainer';
import { ActionEnum } from '../enums/authz-action.enum';
import { FeatureEnum } from '../enums/authz-feature.enum';
import useWindowSize from '../hooks/useWindowSize';
import { ConfigKeysEnum, useConfigKey } from '../services/configKeys';

import { get } from '../services/storage';
import { UserRolesEnum } from '../enums/user-roles.enum';
import { breakpoints } from '../constants/responsive';

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 1em;

  @media (max-width: ${breakpoints.mobile}px) {
    padding: 0.8em;
  }

  @media (max-width: ${breakpoints.small}px) {
    padding: 0.6em;
  }
`;

const Logo = styled(Image)`
  width: 13em;

  @media (max-width: ${breakpoints.mobile}px) {
    width: 10em;
  }

  @media (max-width: ${breakpoints.small}px) {
    width: 8em;
  }

  @media (max-width: ${breakpoints.extraSmall}px) {
    width: 7em;
  }
`;

const MenuCloseIcon = styled(Icon)`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translate(-50%, -50%);
  color: ${props => props.theme.main.color};
  cursor: pointer;
  padding: 8px;
  min-width: 44px; /* Touch target adequado */
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    right: 4px;
  }
`;

const SidebarGrid = styled(Responsive) <{ isCollapsed?: boolean }>`
  background-color: #fff;
  width: ${props => props.isCollapsed ? '70px' : '268px'} !important;
  min-width: ${props => props.isCollapsed ? '70px' : '268px'} !important;
  max-width: ${props => props.isCollapsed ? '70px' : '268px'} !important;
  padding: 0 0 100px;
  border-right: 1px solid #e6e6e6;
  overflow-x: hidden;
  overflow-y: auto;
  box-shadow: none;
  height: 100vh;
  transition: all 0.3s ease;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling no iOS */

  @media (max-width: ${breakpoints.mobile}px) {
    width: 280px !important;
    min-width: 280px !important;
    max-width: 280px !important;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: ${breakpoints.small}px) {
    width: 260px !important;
    min-width: 260px !important;
    max-width: 260px !important;
  }

  @media (max-width: ${breakpoints.extraSmall}px) {
    width: 240px !important;
    min-width: 240px !important;
    max-width: 240px !important;
  }
`;

const VersionText = styled.div`
  position: absolute;
  bottom: 0.5rem;
  width: 100%;

  p {
    text-align: center;
    font-size: 12px;

    @media (max-width: ${breakpoints.mobile}px) {
      font-size: 11px;
    }

    @media (max-width: ${breakpoints.small}px) {
      font-size: 10px;
    }
  }
`;

const AppMenuItemsWrapper = styled.div`
  background-color: #fff;
  border-radius: none;
  font-size: 1em;
  display: flex;
  flex-direction: column;

  .ui.vertical.menu > .active.item:only-child {
    border-radius: none;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    font-size: 0.95em;
  }

  @media (max-width: ${breakpoints.small}px) {
    font-size: 0.9em;
  }
`;

const AppMenu = styled(SemanticMenu)`
  background-color: #e1e8ed !important;

  @media (max-width: ${breakpoints.mobile}px) {
    .item {
      padding: 0.9em 1em !important;
      min-height: 44px; /* Touch target adequado */
    }
  }
`;

const LogoContainerWithCollapse = styled.div<{ isCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: ${props => props.isCollapsed ? '0.5em' : '1em'};
  transition: padding 0.3s ease;
  min-height: 80px;

  @media (max-width: ${breakpoints.mobile}px) {
    padding: 0.8em;
    min-height: 70px;
  }

  @media (max-width: ${breakpoints.small}px) {
    padding: 0.6em;
    min-height: 60px;
  }
  
  .logo {
    transition: all 0.3s ease;
    ${props => props.isCollapsed && `
      width: 40px; 
      height: 40px;
    `}
  }
`;

const mapState = (state: iRootState) => ({
  user: state.user,
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  sidebarCollapse: state.sidebarCollapse
});

const mapDispatch = (dispatch: iRootDispatch) => ({});

export type Props = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch> &
{ handleToggleSidebar: () => void };

const Menu = ({
  user,
  availableBrokersAndEstates,
  sidebarCollapse,
  handleToggleSidebar
}: Props) => {
  const [options, setOptions] = useState<MenuItemProps[]>([]);
  const [canView, setCanView] = useState<boolean>(false);

  const { isMobile } = useWindowSize();

  const { data: configData, isSuccess: configIsSuccess } = useConfigKey(
    ConfigKeysEnum.ACCESS_OPERATIONAL_EMAILS
  );

  useEffect(() => {
    if (configIsSuccess && configData) {
      if (user.email && configData.value.includes(user.email)) {
        setCanView(true);
      }
    }
  }, [configIsSuccess, configData, setCanView, user.email]);

  useEffect(() => {
    let menuOpts = menuOptions.map(menu => ({
      ...menu,
      submenuItems: menu.submenuItems?.map(item => ({ ...item }))
    }));

    if (user.role === UserRolesEnum.estateQuotation) {
      const allowedAliases = [
        'DASHBOARD',
        'FIRE',
        'BOND',
        'CAPITALIZATION',
        'LENDER_INSURANCE',
        'CLAIMS'
      ];
      menuOpts = menuOpts.filter(menu => allowedAliases.includes(menu.alias));
    }

    if (
      user.role === UserRolesEnum.franchiseAdmin ||
      user.role === UserRolesEnum.multiFranchiseAdmin
    ) {
      menuOpts = menuOpts.filter(menu => menu.alias !== 'MANAGEMENT');
    }

    if (user.role?.includes('ESTATE')) {
      menuOpts.forEach(submenu => {
        if (submenu.name === 'insurance') {
          submenu.submenuItems = submenu.submenuItems?.filter(
            submenu => submenu.name !== 'reports-form'
          );
        }

        if (user.role === 'ESTATE_USER' && submenu.name === 'user') {
          submenu.submenuItems = submenu.submenuItems?.filter(
            submenu => submenu.name !== 'new-user'
          );
        }
      });
    }

    if (!availableBrokersAndEstates.enableFireInsuranceMenu) {
      menuOpts = menuOpts.filter(menu => menu.alias !== 'FIRE');
    }

    // Filtrar menu de Prestamista se imobiliária não está habilitada
    // Admins (BROKER_ADMIN, BROKER_ANALYST) sempre têm acesso
    const isAdminUser = user.role === UserRolesEnum.brokerAdmin ||
      user.role === UserRolesEnum.brokerAnalyst;
    if (
      !availableBrokersAndEstates.lenderInsuranceEnabled &&
      !isAdminUser &&
      user.role !== UserRolesEnum.estateQuotation
    ) {
      menuOpts = menuOpts.filter(menu => menu.alias !== 'LENDER_INSURANCE');
    }

    // Filtrar submenu items baseado em role
    menuOpts.forEach(menu => {
      if (menu.submenuItems) {
        menu.submenuItems = menu.submenuItems.filter(item => {
          if ((item as any).requiresRoles?.length) {
            return (item as any).requiresRoles.includes(user.role);
          }
          if ((item as any).requiresRole) {
            return user.role === (item as any).requiresRole;
          }
          return true;
        });
      }
    });

    setOptions(menuOpts);
  }, [
    availableBrokersAndEstates.enableFireInsuranceMenu,
    availableBrokersAndEstates.lenderInsuranceEnabled,
    availableBrokersAndEstates.standaloneChargesEnabled,
    user.role
  ]);

  const userRole = get('role');

  return (
    <SidebarGrid isCollapsed={sidebarCollapse.isCollapsed}>
      <LogoContainerWithCollapse isCollapsed={sidebarCollapse.isCollapsed}>
        <Logo className="logo" src={sidebarCollapse.isCollapsed ? partialLogo : logo} />

        {isMobile && (
          <MenuCloseIcon name="arrow left" size="large" onClick={handleToggleSidebar} />
        )}
      </LogoContainerWithCollapse>

      <AppMenu fluid vertical inverted borderless>
        <AppMenuItemsWrapper>
          {options.map((item: MenuItemProps) => {
            if (['MANAGEMENT'].includes(item.name)) {
              if ((userRole || '').includes('ESTATE')) {
                item.submenuItems = item.submenuItems?.filter(sub => {
                  if (sub.name === 'REPORTS') return true;
                  if (sub.name === 'ESTATE_UNITS') {
                    return userRole === UserRolesEnum.estateAdmin;
                  }
                  if (sub.name === 'STANDALONE_CHARGES') {
                    return Boolean(availableBrokersAndEstates.standaloneChargesEnabled);
                  }
                  return false;
                });
              }
              return <MenuItem key={item.id} {...item} isCollapsed={sidebarCollapse.isCollapsed} />;
            }

            if (
              (['FRANCHISES'].includes(item.name) &&
                userRole === UserRolesEnum.multiFranchiseAdmin) ||
              userRole === UserRolesEnum.franchiseAdmin
            ) {
              const sub = item.submenuItems?.filter(sub => {
                if (userRole === UserRolesEnum.franchiseAdmin) {
                  return (
                    sub.name !== 'new-franchise' &&
                    sub.name !== 'list-master-franchises' &&
                    sub.name !== 'list-franchises'
                  );
                }
                return (
                  sub.name !== 'new-franchise' && sub.name !== 'list-master-franchises'
                );
              });
              item.submenuItems = sub;
              return <MenuItem key={item.id} {...item} isCollapsed={sidebarCollapse.isCollapsed} />;
            }

            // LENDER_INSURANCE não precisa de verificação de permissão, já foi filtrado anteriormente
            if (item.alias === 'LENDER_INSURANCE') {
              return <MenuItem key={item.id} {...item} isCollapsed={sidebarCollapse.isCollapsed} />;
            }

            if (item.alias === 'PROPERTIES') {
              return (
                <MenuItem key={item.id} {...item} isCollapsed={sidebarCollapse.isCollapsed} />
              );
            }

            if (item.name !== 'OPERATIONAL') {
              return (
                <AuthorizationContainer
                  key={item.id}
                  action={ActionEnum.read}
                  feature={item.name}
                >
                  <MenuItem key={item.id} {...item} isCollapsed={sidebarCollapse.isCollapsed} />
                </AuthorizationContainer>
              );
            }

            return canView ? <MenuItem key={item.id} {...item} isCollapsed={sidebarCollapse.isCollapsed} /> : <Fragment />;
          })}
        </AppMenuItemsWrapper>

        {!sidebarCollapse.isCollapsed && (
          <VersionText>
            <p>v{packageInfo.version}</p>
          </VersionText>
        )}
      </AppMenu>
    </SidebarGrid>
  );
};

export default connect(mapState, mapDispatch)(Menu);
